import Clarity from '@microsoft/clarity';
import type { AnalyticsEvent, AnalyticsEventData } from './analytics';
import { appVersion } from './appVersion';
import { displayMode } from './displayMode';
import { currentScreenLayout } from './useScreenLayout';

let initialized = false;

// E2E runners (Playwright, Cypress) set VITE_E2E_TEST=true on the Vite dev
// server so test traffic never creates Clarity sessions. Dev runs are skipped
// too unless VITE_CLARITY_DEV=true opts in, so local testing never shows up
// as live sessions.
export function isLocalRun(): boolean {
  if (import.meta.env.VITE_E2E_TEST === 'true') return true;
  if (import.meta.env.DEV && import.meta.env.VITE_CLARITY_DEV !== 'true') {
    return true;
  }
  return false;
}

export function initClarity(): void {
  if (typeof window === 'undefined') return;
  if (initialized) return;
  if (isLocalRun()) return;
  const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID as
    | string
    | undefined;
  if (!projectId) return;
  try {
    Clarity.init(projectId);
    // The SDK loads the recorder asynchronously. Opt rendered content into
    // replay before it loads; Clarity still masks inputs and dropdowns.
    document.documentElement.removeAttribute('data-clarity-mask');
    document.documentElement.setAttribute('data-clarity-unmask', 'true');
    try {
      Clarity.setTag('app_version', appVersion());
    } catch {
      // The session tag is best-effort; init itself succeeded.
    }
    tagDevice();
    initialized = true;
  } catch {
    // Analytics is best-effort only.
  }
}

/** What most often explains a broken session: the layout, installed app or
 *  browser tab, and whether a service worker (an older deploy's, maybe)
 *  serves the page. */
const DEVICE_TAGS: Record<string, () => string> = {
  layout: currentScreenLayout,
  display_mode: displayMode,
  sw_controlled: () => String(Boolean(navigator.serviceWorker?.controller)),
};

function tagDevice(): void {
  for (const [key, read] of Object.entries(DEVICE_TAGS)) {
    try {
      Clarity.setTag(key, read());
    } catch {
      // Each tag is best-effort on its own.
    }
  }
}

/**
 * Keeps this session's full recording and tags what went wrong, so the
 * dashboard can filter for it. Starts Clarity first: a crash before the app
 * mounts would otherwise go unrecorded. The device tags are set again, as the
 * layout and service worker can have changed since the session started.
 */
export function flagClaritySession(reason: string, message?: string): void {
  if (typeof window === 'undefined') return;
  if (isLocalRun()) return;
  initClarity();
  try {
    if (message) Clarity.setTag('error_message', message);
    tagDevice();
    Clarity.upgrade(reason);
  } catch {
    // Analytics is best-effort only.
  }
}

export function trackClarityEvent(
  name: AnalyticsEvent,
  data?: AnalyticsEventData,
): void {
  if (typeof window === 'undefined') return;
  if (isLocalRun()) return;
  try {
    // Clarity events carry no properties, so record them as session tags
    // instead; the dashboard can then filter recordings by them (ui_mode).
    for (const [key, value] of Object.entries(data ?? {})) {
      Clarity.setTag(key, String(value));
    }
    Clarity.event(name);
  } catch {
    // Analytics is best-effort only.
  }
}

export function resetClarityForTests(): void {
  initialized = false;
}
